/**
 * HOW A SEARCH PARAMETER IS WRITTEN INTO THE ADDRESS BAR, AND READ BACK (customer-portal review #121).
 *
 * TanStack Router's default serialiser JSON-encodes every value. A text value that happens to read
 * as JSON — "6", "6947130688", "true" — is therefore written QUOTED so it survives the round trip:
 * a History search for a phone number sat in the URL as `q="6947130688"`, which is not what anybody
 * typed and not what anybody can paste.
 *
 * Every search parameter this console defines is text (the validators in `router.tsx` read strings
 * and turn anything else into text), so here a value is text unless it is unmistakably JSON: an
 * object or array (`{…}`, `[…]`) or a JSON string (`"…"`, which is how links written by the old
 * serialiser arrive). A number stays the digits it was, `true` stays the word, and a value is
 * written exactly as typed.
 */
import { stringifySearchWith } from "@tanstack/react-router";

/** Parses only values that can only be JSON; everything else is left as the text it was. */
export function parseSearchValue(value: string): unknown {
  if (/^\s*["[{]/.test(value)) return JSON.parse(value) as unknown;
  throw new Error("not JSON: kept as text");
}

/**
 * The address bar's search, as the route validators receive it. Read with `URLSearchParams`
 * rather than the router's own decoder, which turns "6947130688" into a number before any parser
 * sees it — every value here stays the text it was unless it can only be JSON.
 */
export function parseConsoleSearch(searchStr: string): Record<string, unknown> {
  const query: Record<string, unknown> = {};
  for (const [key, value] of new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr)) {
    try {
      query[key] = parseSearchValue(value);
    } catch {
      query[key] = value;
    }
  }
  return query;
}

export const stringifyConsoleSearch = stringifySearchWith(JSON.stringify, parseSearchValue);
